import { useState, useEffect } from 'react'

const API_BASE = 'https://booq.now'
const API_KEY = 'bkr_3cb7642e6240fb9d514cd065ed83dae4cae32d205245238949b140536573552e'

export default function BookingWidget({ slug }) {
  const [step, setStep] = useState('date')
  const [timezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })
  const [availableDays, setAvailableDays] = useState([])
  const [slots, setSlots] = useState([])
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [booking, setBooking] = useState(null)

  const headers = {
    Authorization: `Bearer ${API_KEY}`,
    'Content-Type': 'application/json',
  }

  useEffect(() => {
    const month = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}`
    setLoading(true)
    fetch(
      `${API_BASE}/api/v1/availability?slug=${slug}&timezone=${timezone}&month=${month}`,
      { headers }
    )
      .then((r) => r.json())
      .then((data) => {
        const today = new Date().toISOString().split('T')[0]
        const days = (data.days || []).filter((d) => d >= today)
        setAvailableDays(days)
        // No bookable days this month: jump forward to the next month that has
        // availability rather than getting stuck on an empty calendar.
        if (days.length === 0 && data.nextAvailableDate) {
          const nextMonth = data.nextAvailableDate.slice(0, 7)
          // Only advance to a different month, to avoid a refetch loop.
          if (nextMonth !== month) {
            const [y, m] = nextMonth.split('-').map(Number)
            setViewDate(new Date(y, m - 1, 1))
          }
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [slug, timezone, viewDate])

  useEffect(() => {
    if (!selectedDate) return
    setLoading(true)
    fetch(
      `${API_BASE}/api/v1/availability?slug=${slug}&timezone=${timezone}&date=${selectedDate}`,
      { headers }
    )
      .then((r) => r.json())
      .then((data) => {
        setSlots(data.slots || [])
        setStep('time')
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [selectedDate, slug, timezone])

  function handleSelectSlot(slot) {
    setSelectedSlot(slot)
    setStep('form')
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/api/v1/bookings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          slug,
          startTime: selectedSlot.start,
          visitorName: name,
          visitorEmail: email,
          visitorTimezone: timezone,
          notes: notes || undefined,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Booking failed')
      setBooking(data)
      setStep('confirmed')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  function formatTime(iso) {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  function formatDateLabel(dateStr) {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  function buildCalendar() {
    const year = viewDate.getFullYear()
    const month = viewDate.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const availableSet = new Set(availableDays)
    const now = new Date()
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth()
    const today = now.getDate()

    const cells = []
    for (let i = 0; i < firstDay; i++) {
      cells.push({ day: null })
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      cells.push({
        day: d,
        dateStr,
        available: availableSet.has(dateStr),
        past: isCurrentMonth && d < today,
      })
    }
    return {
      cells,
      label: new Date(year, month).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    }
  }

  if (step === 'confirmed' && booking) {
    return (
      <div className="p-8 text-center">
        <div className="text-neon-lime text-5xl mb-4">&#10003;</div>
        <h3 className="text-2xl text-neutral-100 mb-3">Booking confirmed.</h3>
        <p className="text-neutral-400 text-lg">
          {new Date(booking.startTime).toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}{' '}
          at {formatTime(booking.startTime)} &ndash; {formatTime(booking.endTime)}
        </p>
        <p className="text-neutral-500 text-sm mt-3">
          A confirmation email has been sent to {email}.
        </p>
      </div>
    )
  }

  return (
    <div className="p-8 min-h-[400px]">
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-900/20 border border-red-800/50 text-red-300 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-3 text-red-400 hover:text-red-200 transition-colors"
          >
            &times;
          </button>
        </div>
      )}

      {step === 'date' && (() => {
        const { cells, label } = buildCalendar()
        return (
          <div>
            <h3 className="text-xl text-neutral-100 mb-1">Select a date</h3>
            <p className="text-sm text-neutral-500 mb-6">{label}</p>
            {loading && <p className="text-neutral-500">Loading availability...</p>}
            {!loading && availableDays.length === 0 && (
              <p className="text-neutral-500">
                No availability right now &mdash; please email{' '}
                <a href="mailto:hello@mathosconsult.com" className="text-neon-lime hover:underline">
                  hello@mathosconsult.com
                </a>
                .
              </p>
            )}
            {!loading && availableDays.length > 0 && (
              <div>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <div key={d} className="text-center text-xs text-neutral-500 py-1">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((cell, i) =>
                    cell.day === null ? (
                      <div key={`empty-${i}`} />
                    ) : cell.available && !cell.past ? (
                      <button
                        key={cell.dateStr}
                        onClick={() => setSelectedDate(cell.dateStr)}
                        className="aspect-square flex items-center justify-center rounded-lg text-sm
                          text-neutral-200 hover:bg-neon-lime hover:text-dark-900 transition-colors"
                      >
                        {cell.day}
                      </button>
                    ) : (
                      <div
                        key={cell.dateStr}
                        className="aspect-square flex items-center justify-center rounded-lg text-sm text-neutral-700"
                      >
                        {cell.day}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )
      })()}

      {step === 'time' && (
        <div>
          <button
            onClick={() => { setStep('date'); setSlots([]); setSelectedDate(null) }}
            className="text-sm text-neutral-500 hover:text-neon-lime transition-colors mb-4 inline-block"
          >
            &larr; Back
          </button>
          <h3 className="text-xl text-neutral-100 mb-2">Select a time</h3>
          <p className="text-sm text-neutral-500 mb-6">{formatDateLabel(selectedDate)}</p>
          {loading && <p className="text-neutral-500">Loading times...</p>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {slots.map((slot) => (
              <button
                key={slot.start}
                onClick={() => handleSelectSlot(slot)}
                className="px-4 py-3 rounded-lg border border-dark-600 text-neutral-400
                  hover:border-neon-lime hover:text-neutral-100 transition-colors text-sm"
              >
                {formatTime(slot.start)} &ndash; {formatTime(slot.end)}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'form' && (
        <div>
          <button
            type="button"
            onClick={() => setStep('time')}
            className="text-sm text-neutral-500 hover:text-neon-lime transition-colors mb-4 inline-block"
          >
            &larr; Back
          </button>
          <h3 className="text-xl text-neutral-100 mb-2">Your details</h3>
          <p className="text-sm text-neutral-500 mb-6">
            {formatDateLabel(selectedDate)} at {formatTime(selectedSlot.start)}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-neutral-500 mb-1">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-2 rounded-lg bg-dark-900 border border-dark-600
                  text-neutral-100 placeholder-neutral-600 focus:border-neon-lime focus:outline-none transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-500 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 rounded-lg bg-dark-900 border border-dark-600
                  text-neutral-100 placeholder-neutral-600 focus:border-neon-lime focus:outline-none transition-colors"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-500 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 rounded-lg bg-dark-900 border border-dark-600
                  text-neutral-100 placeholder-neutral-600 focus:border-neon-lime focus:outline-none transition-colors resize-none"
                placeholder="Tell me about your problem..."
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-neon-lime text-dark-900 font-semibold
                hover:bg-neon-yellow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Booking...' : 'Confirm Booking'}
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
